/**
 * Stripe Payment Request (Apple Pay / Google Pay) helpers for checkout.
 *
 * @see https://stripe.com/docs/stripe-js/elements/payment-request-button
 */
import { ABO_PROCESS_NAME } from '../transactions/transaction';

/**
 * Whether the checkout for this transaction process offers Apple Pay / Google Pay.
 *
 * The one answer both sides read: CheckoutPageWithPayment mounts the wallet button from it, and the
 * order panel decides from it whether the payment row may show wallet marks. They used to decide
 * separately (`isAbo` twice, in two spellings), so the listing page could promise a wallet the
 * payment step had switched off.
 *
 * The Abo checkout takes cards only: its renewals are merchant-initiated charges on the saved
 * payment method. The soft-booking checkout does save a wallet payment method for a later
 * off-session charge (SetupIntent, usage off_session) and keeps its wallet button — if that ever
 * proves unreliable, this is the place to change, and the order panel follows.
 *
 * @param {string} processName resolved transaction process name
 * @returns {boolean}
 */
export const checkoutOffersWallets = processName => processName !== ABO_PROCESS_NAME;

/**
 * Whether the wallet button can appear on this host at all.
 *
 * Stripe shows Apple Pay / Google Pay only on hosts registered as payment method domains, and the
 * registered production host is the canonical marketplace host (www.cowork24.ch). The same app is
 * also served under other hosts — the bare cowork24.ch answers without redirecting to www — where
 * the button never appears. Promising a wallet there would be false, so the payment row asks this
 * first. Compares full hosts, port included, so a local build only counts when it runs on the host
 * its REACT_APP_MARKETPLACE_ROOT_URL names.
 *
 * @param {string} marketplaceRootURL config.marketplaceRootURL
 * @param {string} host window.location.host of the current page
 * @returns {boolean}
 */
export const walletsRegisteredForHost = (marketplaceRootURL, host) => {
  if (!marketplaceRootURL || !host) {
    return false;
  }
  try {
    return new URL(marketplaceRootURL).host === host;
  } catch (e) {
    return false;
  }
};

/**
 * Pick a Stripe Payment Request `country` (ISO 3166-1 alpha-2) from marketplace currency.
 * When several countries share a currency, the first match in `supportedCountries` wins.
 *
 * @param {string} currency - ISO currency code (e.g. EUR)
 * @param {Array<{ code: string, currency: string }>} supportedCountries - from config.stripe.supportedCountries
 * @returns {string}
 */
export const getPaymentRequestCountryForCurrency = (currency, supportedCountries) => {
  if (!currency || !Array.isArray(supportedCountries)) {
    return 'US';
  }
  const upper = currency.toUpperCase();
  const match = supportedCountries.find(c => c.currency?.toUpperCase() === upper);
  return match?.code || 'US';
};
